/**
 * Sprint 10 — Client share-links tab (real DB-backed via /api/v1).
 *
 * Replaces the prototype STORE-based client-links flow. HR can:
 *   - Generate a new share link (allowed emails + expiry + optional HR note)
 *   - Copy link URL to clipboard
 *   - Revoke active links
 *   - See per-link access log (who tried, when, outcome)
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Plus, Copy, Eye, Trash2, ShieldCheck, ChevronDown, ChevronRight, KeyRound,
  CheckCircle2, AlertCircle, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { toastEmailDispatch } from '@/lib/emailDispatchToast';
import { Link } from 'react-router-dom';

import { v1Get, v1Post } from '@/lib/api';
import { fmtDateTimeIST } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const splitEmails = (raw) => (raw || '').split(/[,;\n\r\s]+/).map((s) => s.trim()).filter(Boolean);
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const fmt = (d) => {
  if (!d) return '—';
  try { return fmtDateTimeIST(d, { dateStyle: 'medium', timeStyle: 'short' }, d); } catch { return d; }
};

const OUTCOME_BADGE = {
  ok: { v: 'default', label: 'OK' },
  email_mismatch: { v: 'destructive', label: 'Wrong email' },
  expired: { v: 'outline', label: 'Expired' },
  revoked: { v: 'outline', label: 'Revoked' },
  invalid_token: { v: 'destructive', label: 'Invalid token' },
};
const STATUS_BADGE = {
  active: { v: 'default', label: 'Active' },
  revoked: { v: 'outline', label: 'Revoked' },
  expired: { v: 'outline', label: 'Expired' },
};

export function CreateLinkDialog({ open, onOpenChange, candidateId, evaluationId = null }) {
  const qc = useQueryClient();
  const [rawEmails, setRawEmails] = useState('');
  const [expiryDays, setExpiryDays] = useState(14);
  const [requireOtc, setRequireOtc] = useState(false);
  const [hrNote, setHrNote] = useState('');
  const [hrCoverNote, setHrCoverNote] = useState('');
  const [artefactToggles, setArtefactToggles] = useState({}); // { key: bool }

  // Sprint 11.7 — fetch readiness so we can render the gated checklist.
  const readinessQ = useQuery({
    queryKey: ['v1-artefact-readiness', candidateId, evaluationId],
    queryFn: () => v1Get(
      `/candidates/${candidateId}/artefact-readiness?evaluation_id=${encodeURIComponent(evaluationId)}`,
    ),
    enabled: open && !!evaluationId,
    staleTime: 30_000,
  });
  const readiness = readinessQ.data?.items || [];

  // When the dialog opens (or readiness data changes), auto-check ready items.
  // Sprint 11.12 \u2014 we now seed from `ready_strict` so HR only starts with
  // genuinely-ready artefacts pre-selected.
  const lastSeededRef = useRef(null);
  useEffect(() => {
    if (!open) { lastSeededRef.current = null; return; }
    if (!evaluationId) { setArtefactToggles({}); return; }
    if (!readiness.length) return;
    const seedKey = `${evaluationId}:${readinessQ.dataUpdatedAt ?? 0}`;
    if (lastSeededRef.current === seedKey) return;
    lastSeededRef.current = seedKey;
    const seed = {};
    readiness.forEach((it) => { seed[it.key] = !!it.ready_strict; });
    setArtefactToggles(seed);
  }, [open, evaluationId, readinessQ.dataUpdatedAt, readiness]);

  // Factor breakdown requires the panels section (scores live there).
  // AI summary (ai_report) is independent.
  const effectiveToggles = artefactToggles.ai_breakdown && !artefactToggles.panels
    ? { ...artefactToggles, ai_breakdown: false }
    : artefactToggles;

  const emails = splitEmails(rawEmails);
  const invalid = emails.filter((e) => !isValidEmail(e));
  const selectedArtefacts = Object.entries(effectiveToggles).filter(([, v]) => v).map(([k]) => k);
  const noArtefacts = selectedArtefacts.length === 0;

  // Sprint 11.12 \u2014 hard gate: every selected artefact must be `ready_strict`.
  // Mirrors the server-side check on POST /client-share-links.
  const readinessByKey = Object.fromEntries(readiness.map((it) => [it.key, it]));
  const notStrictReady = selectedArtefacts.filter(
    (k) => readinessByKey[k] && !readinessByKey[k].ready_strict
  );
  const allSelectedAreStrictReady = selectedArtefacts.length > 0 && notStrictReady.length === 0;

  const canSubmit = !!evaluationId
    && emails.length > 0
    && invalid.length === 0
    && expiryDays >= 1 && expiryDays <= 90
    && !noArtefacts
    && allSelectedAreStrictReady;

  const mutation = useMutation({
    mutationFn: () => {
      if (!evaluationId) throw new Error('evaluation_id is required');
      return v1Post(`/candidates/${candidateId}/client-share-links`, {
        allowed_emails: emails,
        include_ai_breakdown: false,
        require_otc: requireOtc,
        expires_in_days: expiryDays,
        hr_note: hrNote.trim() || null,
        included_artefacts: selectedArtefacts,
        hr_cover_note: hrCoverNote.trim() || null,
        evaluation_id: evaluationId,
      });
    },
    onSuccess: (link) => {
      toast.success('Share link created — share the URL with the client.');
      toastEmailDispatch(link?.email_dispatch);
      qc.invalidateQueries({ queryKey: ['v1-share-links', candidateId] });
      onOpenChange(false);
      setRawEmails(''); setHrNote(''); setHrCoverNote(''); setRequireOtc(false);
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      // 422 strict-readiness gate: { error: 'artefacts_not_ready', failures: { key: [reasons] } }
      if (d && typeof d === 'object' && d.error === 'artefacts_not_ready' && d.failures) {
        const lines = Object.entries(d.failures).map(([k, reasons]) => {
          const label = readinessByKey[k]?.label || k;
          return `${label}: ${(reasons || []).join(' ')}`;
        });
        toast.error(`Cannot generate \u2014 ${lines.length} artefact(s) not ready`, {
          description: lines.join('\n'),
          duration: 8000,
        });
        return;
      }
      const msg = typeof d === 'string' ? d : d?.message || 'Could not create link.';
      toast.error(msg);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] gap-0 p-0 overflow-hidden flex flex-col"
        data-testid="create-share-link-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Generate client share link</DialogTitle>
          <DialogDescription>
            Creates a unique, email-gated link to this candidate&apos;s report. Each access is logged.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable 2-column body \u2014 max viewport-aware height so the
            footer is always visible. */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 overflow-y-auto px-6 py-5 md:grid-cols-2 flex-1 min-h-0">
          {/* ============ LEFT COLUMN: recipients + access controls ============ */}
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Allowed client emails (comma or newline separated)
              </label>
              <Textarea
                rows={3}
                value={rawEmails}
                onChange={(e) => setRawEmails(e.target.value)}
                placeholder="client.lead@acmecorp.com, hr@acmecorp.com"
                data-testid="share-link-emails-input"
                className="mt-1"
              />
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {emails.map((e) => (
                  <Badge key={e} variant={isValidEmail(e) ? 'secondary' : 'destructive'}>{e}</Badge>
                ))}
              </div>
              {invalid.length > 0 && (
                <p className="mt-1 text-xs text-destructive">Invalid: {invalid.join(', ')}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Expires in (days, 1–90)</label>
              <Input
                type="number" min={1} max={90} value={expiryDays}
                onChange={(e) => setExpiryDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))}
                data-testid="share-link-expiry-input"
                className="mt-1"
              />
            </div>

            {/* HR cover note */}
            <div>
              <label htmlFor="hr-cover-note" className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <MessageSquare className="h-3 w-3 text-primary" /> Cover note (shown to client)
              </label>
              <Textarea
                id="hr-cover-note"
                rows={3}
                value={hrCoverNote}
                onChange={(e) => setHrCoverNote(e.target.value)}
                placeholder="E.g. 'Sharing this candidate for the Senior Backend Engineer role.'"
                data-testid="share-link-cover-note-input"
                maxLength={2000}
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-3 rounded-lg border bg-secondary/40 p-3" data-testid="share-link-require-otc-toggle-row">
              <KeyRound className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold">Require 6-digit OTC code</p>
                <p className="text-[11px] text-muted-foreground">
                  Optional extra step after the email gate.
                </p>
              </div>
              <Switch checked={requireOtc} onCheckedChange={setRequireOtc} data-testid="share-link-require-otc-toggle" />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">HR note (internal, not shown to client)</label>
              <Textarea
                rows={2}
                value={hrNote}
                onChange={(e) => setHrNote(e.target.value)}
                placeholder="Why this link / context for audit"
                data-testid="share-link-note-input"
                className="mt-1"
              />
            </div>
          </div>

          {/* ============ RIGHT COLUMN: artefact checklist ============ */}
          <div className="space-y-3">
            <div className="rounded-lg border bg-card p-3" data-testid="artefact-checklist">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  What will the client see?
                </p>
                <span className="text-[10px] tabular-nums text-muted-foreground">
                  {selectedArtefacts.length} selected
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Completed artefacts are auto-selected. Uncheck any to withhold. Greyed = not ready.
              </p>
              <ul className="mt-2 space-y-1.5">
                {readinessQ.isLoading && <li className="text-xs text-muted-foreground">Loading readiness…</li>}
                {readiness.map((it) => {
                  const blockedByPanels = it.key === 'ai_breakdown' && !effectiveToggles.panels;
                  const checked = !!effectiveToggles[it.key];
                  const strict = !!it.ready_strict;
                  const blocking = it.blocking_reasons || [];
                  // Allow HR to check items even if not strict-ready, so they
                  // see the blocking reasons. The Generate button stays
                  // disabled regardless.
                  const disabled = blockedByPanels;
                  const showBlockingReasons = checked && !strict && blocking.length > 0;
                  return (
                    <li
                      key={it.key}
                      className={`flex items-start gap-2 rounded-md border p-2 ${
                        showBlockingReasons ? 'border-amber-300 bg-amber-50/40' :
                        disabled ? 'opacity-50' : 'hover:bg-secondary/30'
                      }`}
                      data-testid={`artefact-row-${it.key}`}
                    >
                      <input
                        type="checkbox"
                        id={`artefact-${it.key}`}
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => setArtefactToggles((t) => ({ ...t, [it.key]: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 shrink-0"
                        data-testid={`artefact-toggle-${it.key}`}
                      />
                      <label htmlFor={`artefact-${it.key}`} className="flex-1 cursor-pointer">
                        <span className="flex items-center gap-1.5 text-sm font-medium">
                          {it.label}
                          {strict ? (
                            <Badge variant="secondary" className="border-emerald-500/30 bg-emerald-500/10 text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                              <CheckCircle2 className="mr-0.5 h-2.5 w-2.5" /> Ready
                            </Badge>
                          ) : it.ready ? (
                            <Badge variant="outline" className="border-amber-500/40 bg-amber-50 text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-300">
                              <AlertCircle className="mr-0.5 h-2.5 w-2.5" /> Needs work
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-muted-foreground/30 text-[9px] uppercase tracking-wider text-muted-foreground">
                              <AlertCircle className="mr-0.5 h-2.5 w-2.5" /> Not ready
                            </Badge>
                          )}
                        </span>
                        <span className="block text-[11px] text-muted-foreground">{it.detail}</span>
                        {showBlockingReasons && (
                          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] text-amber-700 dark:text-amber-300" data-testid={`artefact-blocking-${it.key}`}>
                            {blocking.map((r, idx) => <li key={idx}>{r}</li>)}
                          </ul>
                        )}
                        {blockedByPanels && (
                          <span className="block text-[10px] text-amber-700 dark:text-amber-300">
                            Requires &ldquo;Panel Evaluation (section)&rdquo; to be included.
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
              {noArtefacts && readiness.length > 0 && (
                <p className="mt-2 text-xs text-destructive" data-testid="artefact-none-selected-error">
                  Select at least one artefact to share with the client.
                </p>
              )}
              {!noArtefacts && notStrictReady.length > 0 && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900" data-testid="strict-gate-warning">
                  <p className="flex items-center gap-1.5 font-semibold">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Cannot generate — {notStrictReady.length} selected item(s) need work
                  </p>
                  <p className="mt-0.5">
                    Uncheck them or complete the missing pieces (see notes under each row).
                    A client link is only generated when every chosen section is client-ready.
                  </p>
                </div>
              )}
              <p className="mt-2 border-t pt-2 text-[11px] text-muted-foreground">
                Client will see: <strong className="text-foreground">
                  {selectedArtefacts.length
                    ? selectedArtefacts.map((k) => readiness.find((r) => r.key === k)?.label || k).join(' \u00b7 ')
                    : 'nothing yet'}
                </strong>
              </p>
            </div>
          </div>
        </div>

        {/* Sticky footer \u2014 always visible regardless of body scroll */}
        <DialogFooter className="px-6 py-4 border-t bg-card shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="share-link-cancel-button">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="gap-1.5"
            data-testid="share-link-submit-button"
          >
            <ShieldCheck className="h-4 w-4" />
            {mutation.isPending ? 'Generating\u2026' : 'Generate link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareLinkCard({ link, candidateId }) {
  const qc = useQueryClient();
  const [logOpen, setLogOpen] = useState(false);
  const { data: detail } = useQuery({
    queryKey: ['v1-share-link-log', link.id],
    queryFn: () => v1Get(`/client-share-links/${link.id}/access-log`),
    enabled: logOpen,
  });
  const revokeMut = useMutation({
    mutationFn: () => v1Post(`/client-share-links/${link.id}/revoke`, { reason: 'HR cancelled' }),
    onSuccess: () => {
      toast.success('Link revoked.');
      qc.invalidateQueries({ queryKey: ['v1-share-links', candidateId] });
    },
  });

  const url = `${window.location.origin}/client/${link.token}`;
  const badge = STATUS_BADGE[link.status] || { v: 'outline', label: link.status };

  return (
    <li className="rounded-xl border bg-card p-4" data-testid={`share-link-${link.id}`}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold">{link.allowed_emails.join(', ')}</p>
            <Badge variant={badge.v}>{badge.label}</Badge>
            {link.require_otc && (
              <Badge variant="outline" className="gap-1" data-testid={`share-link-otc-badge-${link.id}`}>
                <KeyRound className="h-3 w-3" /> OTC
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Expires {fmt(link.expires_at)} · created {fmt(link.created_at)}
          </p>
          {link.hr_note && <p className="mt-1 text-xs italic text-muted-foreground">Note: {link.hr_note}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5"
            onClick={() => { navigator.clipboard?.writeText(url); toast.success('Link copied'); }}
            data-testid={`share-link-copy-${link.id}`}>
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
          <Link to={`/client/${link.token}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="gap-1.5" data-testid={`share-link-preview-${link.id}`}>
              <Eye className="h-3.5 w-3.5" /> Open
            </Button>
          </Link>
          {link.status === 'active' && (
            <Button variant="ghost" size="sm" className="gap-1.5 text-rose-700 hover:bg-rose-50"
              onClick={() => revokeMut.mutate()} disabled={revokeMut.isPending}
              data-testid={`share-link-revoke-${link.id}`}>
              <Trash2 className="h-3.5 w-3.5" /> Revoke
            </Button>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setLogOpen((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
        data-testid={`share-link-toggle-log-${link.id}`}
      >
        {logOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Access log
      </button>
      {logOpen && (
        <div className="mt-2 rounded-lg border bg-secondary/40 p-3 text-xs">
          {!detail ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : detail.access_log.length === 0 ? (
            <p className="text-muted-foreground">No access attempts yet.</p>
          ) : (
            <ul className="space-y-1">
              {detail.access_log.map((a) => {
                const ob = OUTCOME_BADGE[a.outcome] || { v: 'outline', label: a.outcome };
                return (
                  <li key={a.id} className="flex items-center gap-2">
                    <span className="tabular-nums text-muted-foreground">{fmt(a.created_at)}</span>
                    <span className="truncate">{a.email_attempted}</span>
                    <Badge variant={ob.v} className="text-[10px]">{ob.label}</Badge>
                    {a.ip && <span className="ml-auto text-[10px] text-muted-foreground">{a.ip}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

export function ClientShareLinksTab({ candidateId, evaluationId = null }) {
  const [open, setOpen] = useState(false);
  const { data: links, isLoading } = useQuery({
    queryKey: ['v1-share-links', candidateId],
    queryFn: () => v1Get(`/candidates/${candidateId}/client-share-links`),
    enabled: !!candidateId && !!evaluationId,
  });

  if (!evaluationId) {
    return (
      <Card className="card-elevated" data-testid="client-share-links-tab">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Select an evaluation to manage client share links.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-elevated" data-testid="client-share-links-tab">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Client share links</p>
            <p className="text-xs text-muted-foreground">Email-gated, expirable links to the candidate&apos;s client report.</p>
          </div>
          <Button onClick={() => setOpen(true)} className="gap-1.5" data-testid="create-share-link-button">
            <Plus className="h-4 w-4" /> Generate link
          </Button>
        </div>
        <ul className="mt-5 space-y-3">
          {isLoading ? (
            <li className="text-xs text-muted-foreground">Loading…</li>
          ) : (links || []).length === 0 ? (
            <li className="text-xs text-muted-foreground">No share links yet.</li>
          ) : (
            links.map((l) => <ShareLinkCard key={l.id} link={l} candidateId={candidateId} />)
          )}
        </ul>
      </CardContent>
      <CreateLinkDialog open={open} onOpenChange={setOpen} candidateId={candidateId} evaluationId={evaluationId} />
    </Card>
  );
}

export default ClientShareLinksTab;
